// ─── AllDataView ──────────────────────────────────────────────────────────────
// "All Data" dashboard tab: a flat grid of parameter cards, one per parameter
// the user has selected from the left sidebar checkbox tree. Each card shows
// the site name, parameter name, latest numeric value, and unit. Cards are
// drag-reorderable via @dnd-kit/sortable; the order persists per-project in
// localStorage so a user's preferred layout sticks across reloads.
//
// Data:
//   - The list of cards is driven by `selectedParameters` (passed in from the
//     DashboardPage which reads it out of the sidebar's checkbox state).
//   - Latest value for each parameter is fetched via projectsApi.fetchGraphData
//     — same call System Performance + Solid Gauge use. Failures fall back to
//     showing "—" for the value.
//
// Reorder persistence:
//   - localStorage key `wq_alldata_order_p<projectId>` — an array of
//     parameter ids in the user's chosen order. Parameters not in the saved
//     order fall through to their natural sidebar order at the end of the
//     grid (so newly-checked parameters always appear, just at the end).
// ──────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useMemo, useCallback } from 'react'
import { Loader } from 'lucide-react'
import { projectsApi } from '../../lib/projectsApi'
import {
    DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core'
import {
    arrayMove, SortableContext, sortableKeyboardCoordinates,
    useSortable, rectSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

const STORAGE_PREFIX = 'wq_alldata_order_p'

function loadOrder(projectId) {
    try {
        const raw = localStorage.getItem(`${STORAGE_PREFIX}${projectId}`)
        if (!raw) return null
        const parsed = JSON.parse(raw)
        return Array.isArray(parsed) ? parsed : null
    } catch {
        return null
    }
}
function saveOrder(projectId, ids) {
    try {
        localStorage.setItem(`${STORAGE_PREFIX}${projectId}`, JSON.stringify(ids))
    } catch {
        /* quota — ignore */
    }
}

// Format a number with sensible precision.
function fmtValue(v) {
    if (v == null || !Number.isFinite(v)) return '—'
    const abs = Math.abs(v)
    if (abs >= 100) return v.toFixed(0)
    if (abs >= 1)   return (Math.round(v * 100) / 100).toString()
    return (Math.round(v * 1000) / 1000).toString()
}

// Single repositionable parameter card.
function ParamCard({ id, siteName, paramName, value, unit }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        zIndex: isDragging ? 50 : 'auto',
    }
    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className="bg-white rounded shadow-sm border border-gray-100 p-4 cursor-grab active:cursor-grabbing select-none"
            title="Drag to reorder"
        >
            <div className="text-[11px] text-gray-500 leading-tight">{siteName || '—'}</div>
            <div className="text-xs font-semibold text-gray-700 leading-tight mb-3">{paramName}</div>
            <div className="flex items-baseline gap-1.5">
                <span className="text-2xl md:text-3xl font-bold text-[#358ECD] leading-none">{fmtValue(value)}</span>
                {unit && <span className="text-sm text-[#358ECD] font-medium">{unit}</span>}
            </div>
        </div>
    )
}

export default function AllDataView({ selectedParameters = [], projectId }) {
    const [savedOrder, setSavedOrder] = useState(() => loadOrder(projectId))
    const [values, setValues] = useState({})        // id → { value, unit }
    const [loading, setLoading] = useState(false)

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    )

    // Reload the saved order whenever the project changes.
    useEffect(() => {
        setSavedOrder(loadOrder(projectId))
    }, [projectId])

    // Effective render order: saved order first (filtered to currently-selected
    // params, so deselected params disappear) then any newly-checked params at
    // the end. This keeps the grid stable as the user toggles the sidebar.
    const orderedParams = useMemo(() => {
        if (selectedParameters.length === 0) return []
        const byId = new Map(selectedParameters.map((p) => [String(p.id), p]))
        const out = []
        const seen = new Set()
        if (Array.isArray(savedOrder)) {
            for (const id of savedOrder) {
                const key = String(id)
                const p = byId.get(key)
                if (p) { out.push(p); seen.add(key) }
            }
        }
        for (const p of selectedParameters) {
            const key = String(p.id)
            if (!seen.has(key)) out.push(p)
        }
        return out
    }, [selectedParameters, savedOrder])

    // Fetch latest values for every currently-selected parameter. Promise.allSettled
    // so one slow/failing param doesn't block the rest.
    useEffect(() => {
        if (selectedParameters.length === 0) { setValues({}); return }
        let cancelled = false
        setLoading(true)
        Promise.allSettled(
            selectedParameters.map((p) =>
                projectsApi.fetchGraphData(Number(p.id), {})
                    .then((res) => ({
                        id: String(p.id),
                        value: (res && typeof res === 'object' && !res.error) ? (res.latestValue ?? null) : null,
                        unit:  (res && res.unit) || p.unit || '',
                    }))
            )
        )
            .then((results) => {
                if (cancelled) return
                const map = {}
                results.forEach((r) => { if (r.status === 'fulfilled' && r.value?.id) map[r.value.id] = r.value })
                setValues(map)
            })
            .finally(() => { if (!cancelled) setLoading(false) })
        return () => { cancelled = true }
    // Re-run only when the set of selected parameter ids changes — value
    // changes inside selectedParameters don't need to trigger a refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedParameters.map((p) => p.id).join(',')])

    const handleDragEnd = useCallback(({ active, over }) => {
        if (!over || active.id === over.id) return
        const ids = orderedParams.map((p) => String(p.id))
        const from = ids.indexOf(String(active.id))
        const to   = ids.indexOf(String(over.id))
        if (from < 0 || to < 0) return
        const next = arrayMove(ids, from, to)
        setSavedOrder(next.map(Number))
        saveOrder(projectId, next.map(Number))
    }, [orderedParams, projectId])

    // Empty state — nothing selected in the sidebar
    if (selectedParameters.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center text-gray-400 gap-2">
                <span className="text-3xl">📋</span>
                <p className="text-sm">No parameters selected.</p>
                <p className="text-xs text-gray-300">Tick parameter checkboxes in the left sidebar to add cards here.</p>
            </div>
        )
    }

    return (
        <div className="px-4 py-4">
            {loading && (
                <div className="mb-3 inline-flex items-center gap-2 text-xs text-gray-500">
                    <Loader className="w-3.5 h-3.5 animate-spin" /> Loading latest values…
                </div>
            )}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={orderedParams.map((p) => String(p.id))} strategy={rectSortingStrategy}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {orderedParams.map((p) => {
                            const v = values[String(p.id)]
                            return (
                                <ParamCard
                                    key={p.id}
                                    id={String(p.id)}
                                    siteName={p.siteName || p.site_name || ''}
                                    paramName={p.name}
                                    value={v?.value ?? null}
                                    unit={v?.unit || p.unit || ''}
                                />
                            )
                        })}
                    </div>
                </SortableContext>
            </DndContext>
        </div>
    )
}
